import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../lib/api";
import { formatDateTime } from "../lib/money";
import { Spinner } from "../components/ui-modern";
import { Eyebrow, ModernEmptyState, ModernErrorBanner, ModernPagination, ModernStatusPill, ModernTableCard, ModernTd, ModernTh } from "../components/ui-modern";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "new", label: "New" },
  { value: "replied", label: "Replied" },
];
const SOURCE_FILTERS = [
  { value: "", label: "All" },
  { value: "website", label: "Website" },
  { value: "app", label: "In-app" },
];

/**
 * Platform-admin inbox for support_inquiries — every anonymous
 * bay2bill-web submission plus every tenant's in-app one. Mirrors
 * InvoicesListPageModern.tsx's own table/pill-filter/pagination shape.
 */
export function AdminSupportInquiriesListPageModern() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "support-inquiries", status, source, page, pageSize],
    queryFn: () =>
      adminApi.listSupportInquiries({
        status: status || undefined,
        source: source || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
  });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3.5">
        <div>
          <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">{data ? `${data.total} inquiries` : "…"}</Eyebrow>
          <h1 className="m-0 text-[28px] font-bold text-[#071a33]">Support Inquiries</h1>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-5">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => {
                setStatus(f.value);
                setPage(1);
              }}
              className={`h-9 rounded-full border-2 px-3.5 text-[13px] font-semibold ${
                status === f.value ? "border-[#0f6d86] bg-[#e6f1f5] text-[#0b5468]" : "border-transparent bg-[#eef1f6] text-[#4a5361]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {SOURCE_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => {
                setSource(f.value);
                setPage(1);
              }}
              className={`h-9 rounded-full border-2 px-3.5 text-[13px] font-semibold ${
                source === f.value ? "border-[#0f6d86] bg-[#e6f1f5] text-[#0b5468]" : "border-transparent bg-[#eef1f6] text-[#4a5361]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <Spinner />}
      {error && <ModernErrorBanner message="Could not load support inquiries." />}
      {data && data.items.length === 0 && <ModernEmptyState>No inquiries match yet.</ModernEmptyState>}

      {data && data.items.length > 0 && (
        <ModernTableCard>
          <table className="w-full min-w-[700px] border-collapse text-[14px]">
            <thead>
              <tr className="bg-[#f8fafc]">
                <ModernTh>From</ModernTh>
                <ModernTh>Subject</ModernTh>
                <ModernTh>Source</ModernTh>
                <ModernTh>Received</ModernTh>
                <ModernTh>Status</ModernTh>
              </tr>
            </thead>
            <tbody>
              {data.items.map((inq) => (
                <tr
                  key={inq.id}
                  onClick={() => navigate(`/admin/support/${inq.id}`)}
                  className="cursor-pointer transition-colors hover:bg-[#f1f7f9]"
                >
                  <ModernTd>
                    <div className="font-semibold text-[#071a33]">{inq.name}</div>
                    <div className="mt-1 text-[12.5px] text-[#8b97a8]">{inq.email}</div>
                  </ModernTd>
                  <ModernTd>
                    <div className="text-[#071a33]">{inq.subject}</div>
                    {inq.tenantDisplayName && <div className="mt-1 text-[12.5px] text-[#6b7a8f]">{inq.tenantDisplayName}</div>}
                  </ModernTd>
                  <ModernTd>{inq.source === "website" ? "Website" : "In-app"}</ModernTd>
                  <ModernTd>{formatDateTime(inq.createdAt)}</ModernTd>
                  <ModernTd>
                    <ModernStatusPill status={inq.status} />
                    {inq.unreadCount > 0 && (
                      <div className="mt-1 flex items-center gap-1.5 text-[11.5px] font-bold text-[#0f6d86]">
                        <span className="h-[7px] w-[7px] flex-none rounded-full bg-[#0f6d86]" aria-hidden="true" />
                        {inq.unreadCount} unread {inq.unreadCount === 1 ? "message" : "messages"}
                      </div>
                    )}
                  </ModernTd>
                </tr>
              ))}
            </tbody>
          </table>
          <ModernPagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </ModernTableCard>
      )}
    </div>
  );
}
